import { useState, useEffect } from 'react';
import api from '../../services/api';

/**
 * ChangePasswordModal
 *
 * Self-service password change for the currently signed-in user. Posts to
 * POST /api/users/change-my-password — the server derives the uid from the JWT
 * (never the body) and verifies the current password before updating.
 */
export default function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState('');
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const uname = localStorage.getItem('uname') || '';

  // Close on Escape.
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  async function handleSubmit() {
    setError('');
    setMsg('');
    if (!current) return setError('Enter your current password.');
    if (!pwd || !confirm) return setError('Enter and confirm the new password.');
    if (pwd !== confirm) return setError('New passwords do not match.');
    if (pwd.length < 12) return setError('Password must be at least 12 characters long.');

    try {
      setBusy(true);
      await api.post('/users/change-my-password', {
        current_password: current,
        new_password: pwd,
        confirm_password: confirm,
      });
      setMsg('Password updated successfully.');
      setCurrent('');
      setPwd('');
      setConfirm('');
    } catch (err) {
      setError(err?.response?.data?.err || err.message || 'Failed to change password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-xl shadow-lg w-full max-w-md flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cpw-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-slate-700">
          <div>
            <p id="cpw-title" className="font-medium text-gray-900 dark:text-slate-100">Change Password</p>
            {uname && <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Signed in as {uname}</p>}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-3">
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Current password"
            value={current}
            onChange={(e) => { setCurrent(e.target.value); setError(''); setMsg(''); }}
            className={inputClass(error)}
          />
          <input
            type="password"
            autoComplete="new-password"
            placeholder="New password (min 12 characters)"
            value={pwd}
            onChange={(e) => { setPwd(e.target.value); setError(''); setMsg(''); }}
            className={inputClass(error)}
          />
          <input
            type="password"
            autoComplete="new-password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setError(''); setMsg(''); }}
            className={inputClass(error)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
          {msg && <p className="text-xs text-green-600">{msg}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700"
          >
            Close
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

function inputClass(error) {
  return `w-full px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 ${
    error ? 'border-red-400 focus:border-red-400' : 'border-gray-200 dark:border-slate-600 focus:border-blue-400'
  }`;
}
